import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { adminSidebar } from '@/config/sidebarConfig';
import { type UpdateUserPayload, getUserApi, updateUserApi } from '@/lib/api/usersApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { computeAgeCategory, formatDateTimeAbbreviatedFr } from '@shared';
import { FederalClassification, Gender, UserPlan } from '@shared/enums/domain.enum';
import { Role } from '@shared/enums/role.enum';
import type { AuthUser } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, Lock, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import DashboardLayout from '../../components/layout/DashboardLayout';
import LicenseStatusBadge from '../../components/user/LicenseStatusBadge';

// ─── Schéma Zod ──────────────────────────────────────────
const EditSchema = z.object({
  role: z.nativeEnum(Role),
  disabled: z.boolean(),
  firstName: z.string().max(50, 'Prénom trop long (max 50 caractères)'),
  lastName: z.string().max(50, 'Nom trop long (max 50 caractères)'),
  username: z
    .string()
    .max(30, 'Max 30 caractères')
    .refine(v => v === '' || v.length >= 3, { message: 'Minimum 3 caractères si renseigné' }),
  mobile: z
    .string()
    .max(20, 'Max 20 caractères')
    .refine(v => v === '' || /^[+\d\s.\-()]{6,20}$/.test(v), { message: 'Format de téléphone invalide' }),
  licenseNumber: z.string().max(30, 'Max 30 caractères'),
  userPlan: z.nativeEnum(UserPlan),
  birthDate: z.string().optional(),
  gender: z.nativeEnum(Gender).optional(),
  federalClassification: z.nativeEnum(FederalClassification).optional(),
});

type FormData = z.infer<typeof EditSchema>;
type FieldErrors = Partial<Record<keyof FormData, string>>;

// ─── Helpers ─────────────────────────────────────────────

const ROLE_LABELS: Record<Role, string> = {
  [Role.SUPER_ADMIN]: 'Super Admin',
  [Role.ORGANIZATION_ADMIN]: 'Organisateur',
  [Role.CLUB_ADMIN]: 'Club Admin',
  [Role.MEMBER]: 'Joueur',
};

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className='mt-1 flex items-center gap-1 text-xs text-red-600'>
      <AlertCircle className='h-3 w-3 shrink-0' />
      {message}
    </p>
  );
}

function inputCls(hasError?: boolean) {
  return `w-full rounded-lg border px-4 py-3 focus:outline-none transition ${
    hasError ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 focus:border-red-500'
  }`;
}

// ─── EditForm ─────────────────────────────────────────────
function EditForm({ user, isSelf }: { user: AuthUser; isSelf: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormData>({
    role: user.role,
    disabled: user.disabled ?? false,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    username: user.username ?? '',
    mobile: user.mobile ?? '',
    licenseNumber: user.licenseNumber ?? '',
    userPlan: user.userPlan ?? UserPlan.FREE,
    birthDate: user.birthDate ? user.birthDate.slice(0, 10) : '',
    gender: user.gender ?? undefined,
    federalClassification: user.federalClassification ?? undefined,
  });
  const [errors, setErrors] = useState<FieldErrors>({});

  const mutation = useMutation({
    mutationFn: (data: UpdateUserPayload) => updateUserApi(user.id, data),
    onSuccess: () => {
      toast.success('Utilisateur mis à jour');
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
      navigate(ROUTES.ADMIN_USERS);
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour');
    },
  });

  const initialForm = useMemo<FormData>(
    () => ({
      role: user.role,
      disabled: user.disabled ?? false,
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      username: user.username ?? '',
      mobile: user.mobile ?? '',
      licenseNumber: user.licenseNumber ?? '',
      userPlan: user.userPlan ?? UserPlan.FREE,
      birthDate: user.birthDate ? user.birthDate.slice(0, 10) : '',
      gender: user.gender ?? undefined,
      federalClassification: user.federalClassification ?? undefined,
    }),
    [user],
  );

  const isDirty = useMemo(() => {
    return (Object.keys(initialForm) as (keyof FormData)[]).some(k => form[k] !== initialForm[k]);
  }, [form, initialForm]);

  // Block browser/tab close when form is dirty
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  function set<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear field error on change
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
  }

  function validateField(key: keyof FormData) {
    const shape = EditSchema.shape[key];
    const result = shape.safeParse(form[key]);
    if (!result.success) {
      setErrors(prev => ({ ...prev, [key]: result.error.issues[0]?.message }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = EditSchema.safeParse(form);
    if (!result.success) {
      const errs: FieldErrors = {};
      const flat = result.error.flatten().fieldErrors;
      for (const [key, issues] of Object.entries(flat)) {
        if (issues?.[0]) errs[key as keyof FormData] = issues[0];
      }
      setErrors(errs);
      return;
    }
    setErrors({});

    const {
      role,
      disabled,
      firstName,
      lastName,
      username,
      mobile,
      licenseNumber,
      userPlan,
      birthDate,
      gender,
      federalClassification,
    } = result.data;
    const payload: UpdateUserPayload = {
      // Sécurité : ne pas modifier rôle/statut si c'est son propre compte
      ...(isSelf ? {} : { role, disabled }),
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      username: username || undefined,
      mobile: mobile || undefined,
      licenseNumber: licenseNumber || undefined,
      userPlan,
      birthDate: birthDate ? new Date(birthDate).toISOString() : undefined,
      gender: gender || undefined,
      federalClassification: federalClassification || undefined,
    };

    mutation.mutate(payload);
  }

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='min-h-screen'>
        {/* Header sticky */}
        <header className='sticky top-0 z-10 border-b border-slate-200 bg-white'>
          <div className='flex items-center justify-between px-8 py-4'>
            <div className='flex items-center gap-4'>
              <Link
                to={ROUTES.ADMIN_USERS}
                onClick={e => {
                  if (
                    isDirty &&
                    !window.confirm('Vous avez des modifications non sauvegardées. Quitter sans sauvegarder ?')
                  )
                    e.preventDefault();
                }}
                className='rounded-lg p-2 transition hover:bg-slate-100'
              >
                <ArrowLeft className='h-5 w-5 text-slate-600' />
              </Link>
              <div>
                <h1 className='text-2xl font-bold text-slate-900'>Modifier l&apos;utilisateur</h1>
                <p className='text-sm text-slate-500'>
                  {user.firstName} {user.lastName} — {user.email}
                </p>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <Link
                to={ROUTES.ADMIN_USERS}
                onClick={e => {
                  if (
                    isDirty &&
                    !window.confirm('Vous avez des modifications non sauvegardées. Quitter sans sauvegarder ?')
                  )
                    e.preventDefault();
                }}
                className='rounded-lg px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-100'
              >
                Annuler
              </Link>
              <button
                type='submit'
                form='user-edit-form'
                disabled={mutation.isPending}
                className='flex items-center gap-2 rounded-lg bg-red-600 px-6 py-2.5 font-semibold text-white transition hover:bg-red-700 disabled:opacity-60'
              >
                <Save className='h-4 w-4' />
                {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </header>

        {/* Formulaire */}
        <div className='px-8 py-8'>
          <div className='max-w-4xl space-y-6'>
            {/* Bannière propre compte */}
            {isSelf && (
              <div className='flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4'>
                <Lock className='mt-0.5 h-5 w-5 shrink-0 text-amber-600' />
                <div>
                  <p className='font-semibold text-amber-900'>Vous modifiez votre propre compte</p>
                  <p className='mt-0.5 text-sm text-amber-700'>
                    Pour des raisons de sécurité, le rôle et le statut ne peuvent pas être modifiés depuis cette vue.
                    Utilisez un autre compte administrateur si nécessaire.
                  </p>
                </div>
              </div>
            )}

            {/* Erreur de mutation globale */}
            {mutation.isError && (
              <div className='flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
                <AlertCircle className='h-4 w-4 shrink-0' />
                Une erreur est survenue lors de la mise à jour. Veuillez réessayer.
              </div>
            )}

            <form id='user-edit-form' onSubmit={handleSubmit} className='space-y-6'>
              {/* Section : Rôle et permissions */}
              <div className='rounded-xl border border-slate-200 bg-white p-6'>
                <h2 className='mb-4 text-lg font-bold text-slate-900'>Rôle et permissions</h2>
                <div className='grid gap-6 md:grid-cols-2'>
                  {/* Rôle */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>
                      Rôle <span className='text-red-600'>*</span>
                    </label>
                    {isSelf ? (
                      <div className='flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3'>
                        <Lock className='h-4 w-4 text-slate-400' />
                        <span className='text-slate-700'>{ROLE_LABELS[form.role]}</span>
                      </div>
                    ) : (
                      <>
                        <select
                          value={form.role}
                          onChange={e => set('role', e.target.value as Role)}
                          className={inputCls(!!errors.role)}
                        >
                          {Object.values(Role).map(r => (
                            <option key={r} value={r}>
                              {ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                        <FieldError message={errors.role} />
                      </>
                    )}
                    <p className='mt-1.5 text-xs text-slate-500'>
                      Définit les accès et permissions dans l&apos;application
                    </p>
                  </div>

                  {/* Plan joueur */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Plan joueur</label>
                    <select
                      value={form.userPlan}
                      onChange={e => set('userPlan', e.target.value as UserPlan)}
                      className={inputCls(!!errors.userPlan)}
                    >
                      <option value={UserPlan.FREE}>Free (0€/mois)</option>
                      <option value={UserPlan.PLUS}>Plus (1€/mois)</option>
                    </select>
                    <FieldError message={errors.userPlan} />
                  </div>

                  {/* Statut actif/désactivé */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Statut</label>
                    {isSelf ? (
                      <div className='flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3'>
                        <Lock className='h-4 w-4 text-slate-400' />
                        <span className='text-slate-700'>{form.disabled ? 'Désactivé' : 'Actif'}</span>
                      </div>
                    ) : (
                      <div className='flex h-12 items-center gap-6'>
                        <label className='flex cursor-pointer items-center gap-2'>
                          <input
                            type='radio'
                            name='disabled'
                            checked={!form.disabled}
                            onChange={() => set('disabled', false)}
                            className='h-4 w-4 accent-red-600'
                          />
                          <span className='text-sm text-slate-900'>Actif</span>
                        </label>
                        <label className='flex cursor-pointer items-center gap-2'>
                          <input
                            type='radio'
                            name='disabled'
                            checked={form.disabled}
                            onChange={() => set('disabled', true)}
                            className='h-4 w-4 accent-red-600'
                          />
                          <span className='text-sm text-slate-900'>Désactivé</span>
                        </label>
                      </div>
                    )}
                    {isSelf && (
                      <p className='mt-1.5 text-xs text-slate-400'>Impossible de désactiver son propre compte</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section : Informations personnelles */}
              <div className='rounded-xl border border-slate-200 bg-white p-6'>
                <h2 className='mb-4 text-lg font-bold text-slate-900'>Informations personnelles</h2>
                <div className='grid gap-6 md:grid-cols-2'>
                  {/* Prénom */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Prénom</label>
                    <input
                      type='text'
                      value={form.firstName}
                      onChange={e => set('firstName', e.target.value)}
                      onBlur={() => validateField('firstName')}
                      placeholder='Prénom'
                      className={inputCls(!!errors.firstName)}
                    />
                    <FieldError message={errors.firstName} />
                  </div>

                  {/* Nom */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Nom</label>
                    <input
                      type='text'
                      value={form.lastName}
                      onChange={e => set('lastName', e.target.value)}
                      onBlur={() => validateField('lastName')}
                      placeholder='Nom de famille'
                      className={inputCls(!!errors.lastName)}
                    />
                    <FieldError message={errors.lastName} />
                  </div>

                  {/* Username */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Nom d&apos;utilisateur</label>
                    <input
                      type='text'
                      value={form.username}
                      onChange={e => set('username', e.target.value)}
                      onBlur={() => validateField('username')}
                      placeholder='username'
                      className={inputCls(!!errors.username)}
                    />
                    <FieldError message={errors.username} />
                  </div>

                  {/* Email (lecture seule) */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Email</label>
                    <input
                      type='email'
                      value={user.email}
                      disabled
                      className='w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-400'
                    />
                    <p className='mt-1.5 text-xs text-slate-400'>Non modifiable depuis cette vue</p>
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Téléphone</label>
                    <input
                      type='tel'
                      value={form.mobile}
                      onChange={e => set('mobile', e.target.value)}
                      onBlur={() => validateField('mobile')}
                      placeholder='06 12 34 56 78'
                      className={inputCls(!!errors.mobile)}
                    />
                    <FieldError message={errors.mobile} />
                  </div>

                  {/* Numéro de licence */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>N° de licence FFPJP</label>
                    <input
                      type='text'
                      value={form.licenseNumber}
                      onChange={e => set('licenseNumber', e.target.value)}
                      onBlur={() => validateField('licenseNumber')}
                      placeholder='Ex : FPJ-12345'
                      className={inputCls(!!errors.licenseNumber)}
                    />
                    <FieldError message={errors.licenseNumber} />
                    {/* Badge basé sur la valeur sauvegardée (user.licenseNumber), pas sur la saisie en cours */}
                    {user.licenseNumber && (
                      <div className='mt-2'>
                        <LicenseStatusBadge licenseNumber={user.licenseNumber} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section : Données fédérales FFPJP */}
              <div className='rounded-xl border border-slate-200 bg-white p-6'>
                <h2 className='mb-1 text-lg font-bold text-slate-900'>Données fédérales (FFPJP)</h2>
                <p className='mb-4 text-sm text-slate-500'>
                  Utilisées pour les exports GC et les filtres de catégorie sur les concours.
                </p>
                <div className='grid gap-6 md:grid-cols-2'>
                  {/* Date de naissance */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Date de naissance</label>
                    <input
                      type='date'
                      value={form.birthDate ?? ''}
                      onChange={e => set('birthDate', e.target.value)}
                      className={inputCls()}
                    />
                    {form.birthDate && (
                      <p className='mt-1.5 text-xs text-blue-600 font-semibold'>
                        Catégorie : {computeAgeCategory(new Date(form.birthDate))}
                      </p>
                    )}
                  </div>

                  {/* Sexe */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Sexe</label>
                    <select
                      value={form.gender ?? ''}
                      onChange={e => set('gender', (e.target.value as Gender) || undefined)}
                      className={inputCls()}
                    >
                      <option value=''>— Non renseigné —</option>
                      <option value={Gender.MALE}>Masculin</option>
                      <option value={Gender.FEMALE}>Féminin</option>
                    </select>
                  </div>

                  {/* Classification fédérale (admin only) */}
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>
                      Classification fédérale
                      <span className='ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-normal text-slate-500'>
                        admin
                      </span>
                    </label>
                    <select
                      value={form.federalClassification ?? ''}
                      onChange={e =>
                        set('federalClassification', (e.target.value as FederalClassification) || undefined)
                      }
                      className={inputCls()}
                    >
                      <option value=''>— Non renseignée —</option>
                      <option value={FederalClassification.ELITE}>Élite</option>
                      <option value={FederalClassification.HONNEUR}>Honneur</option>
                      <option value={FederalClassification.PROMOTION}>Promotion</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Section : Informations lecture seule */}
              <div className='rounded-xl border border-slate-200 bg-white p-6'>
                <h2 className='mb-4 text-lg font-bold text-slate-900'>Informations du compte</h2>
                <div className='grid gap-6 md:grid-cols-2'>
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Date d&apos;inscription</label>
                    <input
                      type='text'
                      value={formatDateTimeAbbreviatedFr(user.createdAt)}
                      disabled
                      className='w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-400'
                    />
                  </div>
                  <div>
                    <label className='mb-2 block text-sm font-semibold text-slate-900'>Dernière connexion</label>
                    <input
                      type='text'
                      value={formatDateTimeAbbreviatedFr(user.lastLogin)}
                      disabled
                      className='w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-400'
                    />
                  </div>
                  {user.club?.name && (
                    <div>
                      <label className='mb-2 block text-sm font-semibold text-slate-900'>Club</label>
                      <input
                        type='text'
                        value={user.club.name}
                        disabled
                        className='w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-400'
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Actions bas de page */}
              <div className='flex items-center justify-between border-t border-slate-200 pt-6'>
                <button
                  type='button'
                  className='flex items-center gap-2 rounded-lg border border-red-200 px-6 py-3 font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40'
                  disabled={isSelf}
                  onClick={() => toast.error('Suppression non disponible depuis ce formulaire')}
                  title={isSelf ? 'Impossible de supprimer son propre compte' : undefined}
                >
                  <Trash2 className='h-4 w-4' />
                  Supprimer le compte
                </button>
                <div className='flex items-center gap-4'>
                  <Link
                    to={ROUTES.ADMIN_USERS}
                    onClick={e => {
                      if (
                        isDirty &&
                        !window.confirm('Vous avez des modifications non sauvegardées. Quitter sans sauvegarder ?')
                      )
                        e.preventDefault();
                    }}
                    className='rounded-lg px-6 py-3 font-semibold text-slate-700 transition hover:bg-slate-100'
                  >
                    Annuler
                  </Link>
                  <button
                    type='submit'
                    disabled={mutation.isPending}
                    className='rounded-lg bg-red-600 px-8 py-3 font-semibold text-white transition hover:bg-red-700 disabled:opacity-60'
                  >
                    {mutation.isPending ? 'Enregistrement…' : 'Enregistrer les modifications'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// ─── Shell — charge l'utilisateur puis délègue à EditForm ──────────────────
export default function AdminUserEdit() {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const currentUser = useAuthStore(s => s.user);

  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.users.detail(userId),
    queryFn: () => getUserApi(userId),
    enabled: !isNaN(userId),
  });

  if (isLoading) {
    return (
      <DashboardLayout sidebarConfig={adminSidebar}>
        <div className='flex min-h-screen items-center justify-center'>
          <p className='text-slate-500'>Chargement…</p>
        </div>
      </DashboardLayout>
    );
  }

  if (isError || !user) {
    return (
      <DashboardLayout sidebarConfig={adminSidebar}>
        <div className='flex min-h-screen flex-col items-center justify-center gap-4'>
          <p className='text-red-600'>Utilisateur introuvable.</p>
          <Link to={ROUTES.ADMIN_USERS} className='text-sm text-slate-600 underline'>
            Retour à la liste
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const isSelf = currentUser?.id === user.id;

  // key={user.id} : remount propre si l'id change dans l'URL
  return <EditForm key={user.id} user={user} isSelf={isSelf} />;
}
